import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, X, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AdminCarryOverDigest — admin-side companion to the staff CarryOverAlert.
 *
 * Shows a yellow bell-icon button with a count badge whenever the system
 * has auto-closed stale breaks/visits today. Clicking opens a slide-over
 * panel listing every affected staff member, grouped, sorted by most-
 * affected first.
 *
 * Pulls `/api/admin/carryovers` on mount + every 90s + on demand. Backend
 * scopes the data via `get_visible_user_ids` so each manager sees only
 * their team's misses.
 */
export default function AdminCarryOverDigest() {
  const [data, setData] = useState({ total: 0, staff: [], date: '' });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/admin/carryovers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) return;
      const d = await r.json();
      setData({ total: d.total || 0, staff: d.staff || [], date: d.date || '' });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchData();
    const i = setInterval(fetchData, 90_000);
    return () => clearInterval(i);
  }, [fetchData]);

  if (data.total === 0 && !open) {
    // Render a subtle indicator only when there's nothing — keeps the
    // header tidy; clicking still opens the empty-state panel.
    return null;
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="relative p-2 text-amber-600 hover:bg-amber-50 rounded-lg"
        title={`${data.total} carry-over${data.total === 1 ? '' : 's'} today`}
        data-testid="admin-carryover-btn"
      >
        <AlertTriangle size={18} />
        {data.total > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold flex items-center justify-center bg-amber-600 text-white"
            data-testid="admin-carryover-badge"
          >{data.total > 99 ? '99+' : data.total}</span>
        )}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[60] bg-black/40 flex items-start justify-end"
          onClick={() => setOpen(false)}
          data-testid="admin-carryover-overlay"
        >
          <div
            className="bg-white w-full max-w-md h-full overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-amber-50 border-b border-amber-200 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="text-amber-700" size={18} />
                <div>
                  <h3 className="font-semibold text-amber-900">Carry-over digest</h3>
                  <p className="text-[11px] text-amber-700">
                    Stale breaks/visits auto-closed on {data.date || 'today'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={fetchData}
                  disabled={loading}
                  className="p-1.5 text-amber-700 hover:bg-amber-100 rounded"
                  title="Refresh"
                  data-testid="admin-carryover-refresh"
                >
                  <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                </button>
                <button
                  onClick={() => setOpen(false)}
                  className="p-1.5 text-amber-700 hover:bg-amber-100 rounded"
                  data-testid="admin-carryover-close"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            <div className="p-4">
              {data.total === 0 && (
                <p className="text-sm text-gray-500 text-center py-8" data-testid="admin-carryover-empty">
                  No carry-overs today. Your team is wrapping up properly.
                </p>
              )}
              {data.staff.map((s) => (
                <div
                  key={s.staff_id}
                  className="mb-3 border border-amber-200 rounded-lg overflow-hidden"
                  data-testid={`admin-carryover-row-${s.staff_id}`}
                >
                  <div className="px-3 py-2 bg-amber-50 flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="font-semibold text-sm text-gray-900 truncate">
                        {s.name}
                        {s.staff_code && <span className="ml-1.5 text-[10px] text-gray-500 font-mono">{s.staff_code}</span>}
                      </p>
                      <p className="text-[11px] text-gray-500">{s.mobile}</p>
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px]">
                      {s.breaks > 0 && (
                        <span className="px-1.5 py-0.5 rounded bg-orange-200 text-orange-800 font-semibold">
                          {s.breaks} break{s.breaks === 1 ? '' : 's'}
                        </span>
                      )}
                      {s.visits > 0 && (
                        <span className="px-1.5 py-0.5 rounded bg-blue-200 text-blue-800 font-semibold">
                          {s.visits} visit{s.visits === 1 ? '' : 's'}
                        </span>
                      )}
                    </div>
                  </div>
                  <ul className="divide-y divide-gray-100">
                    {s.items.map((it, i) => (
                      <li key={i} className="px-3 py-1.5 flex items-baseline gap-2 text-xs">
                        <span className="px-1.5 py-0.5 rounded bg-gray-100 font-mono text-[10px] uppercase text-gray-600">
                          {it.kind}
                        </span>
                        <span className="text-gray-800 truncate flex-1">{it.label}</span>
                        {it.date && <span className="text-gray-500">· {it.date}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
